import React from 'react'

import { Link } from 'gatsby'
import htmr from 'htmr'
import PropTypes from 'prop-types'
import { CarouselProvider, Slider, Slide, ButtonBack, ButtonNext } from 'pure-react-carousel'
import styled from 'styled-components'
import 'pure-react-carousel/dist/react-carousel.es.css'

import { above, colors, space } from '../../style/design-system'
import Chevron from '../uma-svgs/Chevron'
import VideoLazyload from './VideoLazyload'
import YouTubeEmbed from './YouTubeEmbed'

const TestimonialSlider = ({ className, employer, testimonials, heading }) => {
  const generateTestimonials = (arr) =>
    arr.map(({ node }, idx) => {
      // Removes brackets from ellipses and the space immediately before the ellipses
      node.excerpt = node.excerpt.replace(/ \[&hellip;\]/, '...')

      return (
        <Slide index={idx} key={idx}>
          <div className="container review__wrapper">
            <div className="row d-lg-flex align-items-center">
              <div className="col-12 col-lg-6 review__video">
                <VideoLazyload
                  displayPlayIcon
                  className="video-wrapper"
                  posterAlt={`${node.metaReviewerName} Testimonial Video Poster`}
                  posterGatsbyImg={{
                    image: node.featuredImage.node.localFile.childImageSharp.gatsbyImageData,
                  }}>
                  <YouTubeEmbed autoplay title="UMA" videoId={node.metaReviewerVideo} />
                </VideoLazyload>
              </div>
              <div className="col-12 col-lg-6 review__content">
                {htmr(node.excerpt)}
                <div>
                  <p>
                    <strong>{node.metaReviewerName}</strong>
                    {!employer && ' | UMA Graduate'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Slide>
      )
    })

  return (
    <div className={`${className} py-9 py-lg-10`}>
      <div className="container">
        <div className="h1 text-center mb-5">{heading}</div>
        <div className="row">
          <div className="offset-lg-1 col-12 col-lg-10">
            <div className="quote-line--top">
              <span>&ldquo;</span>
            </div>
          </div>
        </div>
        <CarouselProvider naturalSlideWidth={100} isIntrinsicHeight totalSlides={testimonials.length}>
          <div className="row">
            <div className="d-none d-lg-flex col-lg-1">
              <ButtonBack className="btn--strip">
                <Chevron color="#fff" direction="west" width="45px" />
              </ButtonBack>
            </div>
            <div className="col-12 col-lg-10">
              <Slider className="pointer slider-comp">{generateTestimonials(testimonials)}</Slider>
            </div>
            <div className="d-none d-lg-flex col-lg-1">
              <ButtonNext className="btn--strip">
                <Chevron color="#fff" direction="east" width="45px" />
              </ButtonNext>
            </div>
          </div>
        </CarouselProvider>
        <div className="row">
          <div className="offset-lg-1 col-12 col-lg-10">
            <div className="quote-line--bottom">
              <span className="inner" />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 text-center">
            <Link className="btn btn-outline-white" to="/students/uma-reviews/">
              Testimonials&nbsp;&gt;
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

TestimonialSlider.propTypes = {
  className: PropTypes.string,
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      node: PropTypes.shape({
        excerpt: PropTypes.string.isRequired,
        metaReviewerName: PropTypes.string.isRequired,
        metaReviewerVideo: PropTypes.string,
        featuredImage: PropTypes.object,
      }),
    }),
  ).isRequired,
}

export default styled(TestimonialSlider)`
  align-items: center;
  background-color: ${(props) => (props.backgroundColor ? props.backgroundColor : colors.dev.blue.d())};
  color: #fff;
  display: flex;
  p {
    color: #fff;
  }
  .quote-line--top {
    align-items: center;
    display: flex;
    font-weight: bold;
    font-size: 5.625rem;
    line-height: 0.5;
    margin: 40px 0;
    &::before,
    &::after {
      background-color: #fff;
      content: '';
      display: block;
      flex: auto;
      height: ${space.b};
      border-radius: 1px;
    }
    &::before {
      margin-right: 10px;
    }
    &::after {
      margin-left: 10px;
    }
    span {
      transform: translateY(10px);
    }
  }
  button {
    transition: 0.2s all ease-in-out;
  }
  button[disabled] {
    opacity: 0.5;
  }
  .review__content {
    margin-top: ${space.f};
    ${above.lg`
      margin-top: 0;
    `}
  }
  .quote-line--bottom {
    align-items: flex-start;
    display: flex;
    margin: 2.5rem 0;
    overflow: hidden;
    &::before,
    &::after {
      background-color: #fff;
      content: '';
      display: block;
      flex: auto;
      height: ${space.b};
      border-radius: 1px;
    }
    &::before {
      margin-right: ${space.b};
    }
    &::after {
      margin-left: ${space.b};
    }
    .inner {
      background-color: transparent;
      border: ${space.b} solid #fff;
      height: 40px;
      transform: rotate(-45deg) translate(54%, 14%);
      transform-origin: bottom left;
      width: 40px;
    }
  }

  // IE10+ only
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    .slider-comp {
      min-height: 500px;
    }
  }
`
